// How a visitor reached the site, kept so a booking can say which channel brought it.
//
// Model: the last visit whose URL carried utm_* tags, remembered for 30 days. An untagged
// visit never replaces a tagged one, so someone who scans the QR code, leaves, and types
// the address a week later is still credited to the QR code. With nothing remembered, an
// untagged arrival from another site is kept as a referral (its host only, never the
// full URL, which can carry search terms or tokens).
//
// Storage can be blocked or throw (private windows, disabled site data), so every access
// is guarded and a booking made on the landing page still carries the page's own tags.

export interface Attribution {
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  utmContent?: string
  utmTerm?: string
  landingPage?: string
  referrerHost?: string
}

interface StoredTouch {
  attribution: Attribution
  expiresAt: number
}

export const ATTRIBUTION_STORAGE_KEY = 'eha.attribution'
export const ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000

// The CMS applies the same caps; trimming here keeps a crafted URL from bloating storage.
const UTM_MAX = 100
const LANDING_MAX = 255

const UTM_PARAMS: [keyof Attribution, string][] = [
  ['utmSource', 'utm_source'],
  ['utmMedium', 'utm_medium'],
  ['utmCampaign', 'utm_campaign'],
  ['utmContent', 'utm_content'],
  ['utmTerm', 'utm_term'],
]

// The landing page keeps only the path and the parameters that say where the visitor
// came from and which seminar page they saw. Everything else is dropped: ad click IDs
// (fbclid, gclid) and email subscriber IDs (mc_eid) identify a person, and short links
// deliberately carry click IDs onto the rewritten URL.
const KEPT_LANDING_PARAMS = new Set(['audience', ...UTM_PARAMS.map(([, param]) => param)])

const landingPath = (pathname: string, search: string) => {
  const kept = new URLSearchParams()
  new URLSearchParams(search).forEach((value, key) => {
    if (KEPT_LANDING_PARAMS.has(key)) kept.append(key, value)
  })
  const query = kept.toString()
  return query ? `${pathname}?${query}` : pathname
}

const clip = (value: string | null, max: number) => {
  const s = (value ?? '').trim().slice(0, max)
  return s || undefined
}

const externalReferrerHost = (referrer: string, ownHost: string) => {
  if (!referrer) return undefined
  try {
    const host = new URL(referrer).host.toLowerCase()
    return host && host !== ownHost.toLowerCase() ? host : undefined
  } catch {
    return undefined
  }
}

/** The touch this page view represents, or null when it carries nothing worth keeping. */
export const touchFromLocation = (
  pathname: string,
  search: string,
  referrer: string,
  ownHost: string
): Attribution | null => {
  const params = new URLSearchParams(search)
  const attribution: Attribution = {}
  for (const [field, param] of UTM_PARAMS) {
    const value = clip(params.get(param), UTM_MAX)
    if (value) attribution[field] = value
  }
  const tagged = Object.keys(attribution).length > 0
  const referrerHost = externalReferrerHost(referrer, ownHost)
  if (!tagged && !referrerHost) return null
  return {
    ...attribution,
    landingPage: clip(landingPath(pathname, search), LANDING_MAX),
    ...(referrerHost ? { referrerHost } : {}),
  }
}

const isTagged = (a: Attribution) => UTM_PARAMS.some(([field]) => Boolean(a[field]))

// Storage is writable by anything on the origin, so its shape is checked, not assumed.
const isStoredTouch = (value: unknown): value is StoredTouch => {
  if (!value || typeof value !== 'object') return false
  const { attribution, expiresAt } = value as Record<string, unknown>
  if (typeof expiresAt !== 'number' || !attribution || typeof attribution !== 'object') return false
  return Object.values(attribution).every((v) => typeof v === 'string')
}

const readStored = (storage: Storage, now: number): Attribution | null => {
  try {
    const raw = storage.getItem(ATTRIBUTION_STORAGE_KEY)
    if (!raw) return null
    const stored: unknown = JSON.parse(raw)
    if (!isStoredTouch(stored) || stored.expiresAt <= now) {
      storage.removeItem(ATTRIBUTION_STORAGE_KEY)
      return null
    }
    return stored.attribution
  } catch {
    return null
  }
}

/**
 * Decides whether this page view's touch replaces the remembered one. A tagged touch
 * always wins; a referral only fills an empty slot.
 */
export const nextTouch = (stored: Attribution | null, current: Attribution | null) => {
  if (!current) return null
  if (isTagged(current)) return current
  return stored ? null : current
}

const safeStorage = (): Storage | null => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch {
    return null
  }
}

/** Runs once on app load, after any short-link rewrite, so the tags it sees are final. */
export const recordAttribution = (now = Date.now()) => {
  if (typeof window === 'undefined') return
  const { pathname, search, host } = window.location
  const current = touchFromLocation(pathname, search, document.referrer, host)
  const storage = safeStorage()
  if (!storage) return
  const next = nextTouch(readStored(storage, now), current)
  if (!next) return
  try {
    const touch: StoredTouch = { attribution: next, expiresAt: now + ATTRIBUTION_TTL_MS }
    storage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(touch))
  } catch {
    // Quota or blocked storage: the booking falls back to the current URL.
  }
}

/**
 * What a booking sends. Always an object, even when empty: an empty object tells the
 * CMS the site looked and found a direct visit, which it keeps apart from bookings made
 * before this existed.
 */
export const currentAttribution = (now = Date.now()): Attribution => {
  const storage = safeStorage()
  const stored = storage ? readStored(storage, now) : null
  if (stored) return stored
  if (typeof window === 'undefined') return {}
  const { pathname, search, host } = window.location
  return touchFromLocation(pathname, search, document.referrer, host) ?? {}
}
